import { UserMessage, toUserMessageDate } from "@/bt-common/schemas/bt/user-message";
import { ExtendedUserMessage } from "@common/models/extend-user-message";
import { userMessagesSelectByQuery, userMessagesSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { confirmationDialogOption, EventPL } from "@common-views/calendar/calendar-view";
import { scopeForOption } from "@common-views/calendar/recurring-dialog-options";
import { RRuleRecurrenceHandler } from "@common/services/utils/rrule-recurrence-handler";
import { deleteUserMessage, scheduleMessage, ScheduleMessageThunkArg } from "@common/thunks/UserMessagesThunks";
import { applyRecurringEditPlan } from "@common/thunks/recurring-edit-plan";
import { storeRecurringReplacement } from "@common/thunks/recurring-replacement";
import { buildSplitOccurrenceFields } from "@common/thunks/split-occurrence-document";
import { originalRecurrenceFields } from "@common/thunks/recurring-edit-patch-document";
import { CommonRootState } from "@common/types/common-root-state-type";
import { getWaChatContactDisplayName } from "@/common/utils/display-name-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { accessFormPopupActions } from "./AccessFormPopupReducer";
import { addScheduledMessageButtonActions } from "./AddScheduledMessageButtonReducer";
import { scheduledMessageEditorActions } from "./ScheduledMessageEditorReducer";
import { setSelectedEventDate } from "./CalendarReducer";
import { selectUserObject } from "./UserSelectors";

type HandleNewMessageDialogThunkParams = {
    slotInfo?: any;
    /** BE#657 (a) — open a TRULY FRESH compose: no dueDate seed and dueDateSetByUser:false, so the
     *  summary reads "pick a date / pick a time" and Send stays gated until the user picks. The
     *  "New message" FAB passes this (it carries no time context); explicit calendar slot/day clicks
     *  do NOT, so they keep seeding the clicked time as an explicit choice. */
    fresh?: boolean;
};

export const handleNewMessageDialogThunk = createAsyncThunk<any, HandleNewMessageDialogThunkParams, { state: CommonRootState }>(
    "campaigns/removeHandleAndConnectedEdgesByHandleIdArrayThunk",
    async ({ slotInfo, fresh }, { getState, dispatch }) => {
        const state = getState();
        const user = selectUserObject(state);
        const currentChat = state.WhatsAppReducer.currentChat;

        if (!user) {
            dispatch(accessFormPopupActions.setStatus("Signup"));
            return;
        }

        const contactList = [{
            ...currentChat,
            id: currentChat?.id?._serialized ?? '',
            name: getWaChatContactDisplayName(currentChat),
        }];

        // BE#657 (a) — the "New message" FAB has no time context. Open a fresh compose with NO dueDate
        // seed (so the picker is empty, the summary says "pick a date/time", Send is gated, and
        // opening/closing the time popover commits nothing). dueDateSetByUser stays false.
        if (fresh) {
            const freshMessage = {
                message: "",
                isRecurring: false,
                cancelIfReceived: false,
                contactList,
                calendarMessage: true,
                dueDateSetByUser: false,
            };
            dispatch(scheduledMessageEditorActions.loadMessageData(freshMessage));
            dispatch(addScheduledMessageButtonActions.editMessage(freshMessage as ExtendedUserMessage));
            return;
        }

        let slotStart = moment(state.WhatsAppReducer.hoveredDate ?? slotInfo?.start);

        slotStart = slotStart.isSame(moment(), 'day')
            ? slotStart.set({ hour: moment().hour(), minute: moment().minute(), second: moment().second() })
            : slotStart.set({ hour: 12, minute: 0, second: 0 });

        const newMessage = {
            dueDate: slotStart.startOf("minute").toDate(),
            message: "",
            isRecurring: false,
            cancelIfReceived: false,
            contactList,
            calendarMessage: true,
            dueDateSetByUser: true, // BE#657 M2 — a calendar slot/+New click is an explicit time choice
        };

        dispatch(scheduledMessageEditorActions.loadMessageData(newMessage));
        dispatch(addScheduledMessageButtonActions.editMessage(newMessage as ExtendedUserMessage));
    }
);

type HandleNewMessageThunkParams = {
    slotStart: moment.Moment;
};

export const handleNewMessageThunk = createAsyncThunk<any, HandleNewMessageThunkParams, { state: CommonRootState }>(
    "campaigns/removeHandleAndConnectedEdgesByHandleIdArrayThunk",
    async ({ slotStart }, { getState, dispatch }) => {
        const state = getState();
        const user = selectUserObject(state);
        const currentChat = state.WhatsAppReducer.currentChat;

        if (!user) {
            dispatch(accessFormPopupActions.setStatus("Signup"));
            return;
        }

        const newMessage = {
            dueDate: slotStart.startOf("minute").toDate(),
            message: "",
            isRecurring: false,
            cancelIfReceived: false,
            contactList: [{
                ...currentChat,
                id: currentChat?.id?._serialized ?? '',
                name: getWaChatContactDisplayName(currentChat),
            }],
            calendarMessage: true,
            dueDateSetByUser: true, // BE#657 M2 — a calendar slot/+New click is an explicit time choice
        };

        dispatch(scheduledMessageEditorActions.loadMessageData(newMessage));
        dispatch(addScheduledMessageButtonActions.editMessage(newMessage as ExtendedUserMessage));
    }
);

type HandleClickWeedTimeSlotThunkParams = {
    event: React.MouseEvent<HTMLDivElement, MouseEvent>;
    date: any;
};

export const handleClickWeedTimeSlotThunk = createAsyncThunk<any, HandleClickWeedTimeSlotThunkParams, { state: CommonRootState }>(
    "campaigns/removeHandleAndConnectedEdgesByHandleIdArrayThunk",
    async ({ event, date }, { getState, dispatch }) => {
        const state = getState();
        const user = selectUserObject(state);
        const currentChat = state.WhatsAppReducer.currentChat;

        event.stopPropagation();
        // In week/day view, use the actual slot time that was clicked (date already has
        // correct hour/minute)
        const dueDate = moment(date).startOf("minute");

        if (!user) {
            dispatch(accessFormPopupActions.setStatus("Signup"));
            return;
        }

        const newMessage = {
                dueDate: dueDate.startOf("minute").toDate(),
                message: "",
                isRecurring: false,
                cancelIfReceived: false,
                contactList: [{
                    ...currentChat,
                    id: currentChat?.id?._serialized ?? '',
                    name: getWaChatContactDisplayName(currentChat),
                }],
                calendarMessage: true,
            dueDateSetByUser: true, // BE#657 M2 — a calendar slot/+New click is an explicit time choice
            };

        dispatch(scheduledMessageEditorActions.loadMessageData(newMessage));
        dispatch(addScheduledMessageButtonActions.editMessage(newMessage as ExtendedUserMessage));
    }
);

const handler = new RRuleRecurrenceHandler();

export interface DeleteRecurringDialogCloseThunkParams {
    value: confirmationDialogOption;
    event: EventPL;
}

// The mapping itself now lives beside the values it maps (recurring-dialog-options),
// so a test can drive this path through the REAL translation instead of a copy of it.
const mapDialogOptionToScope = scopeForOption;

export const onDeleteRecurringDialogCloseThunk = createAsyncThunk<any, DeleteRecurringDialogCloseThunkParams, { state: CommonRootState }>(
    "calendar/onDeleteRecurringDialogClose",
    async ({ value, event }, { getState, dispatch }) => {
        if (!value) return;

        const state = getState();
        const messages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            $or: [{ deleted: { $exists: false } }, { deleted: false }],
        });

        const original = messages.find((m) => m._id === event._id);
        if (!original?.rruleset) {
            console.log('[DELETE_RECURRING] No original message or rruleset found');
            return;
        }

        try {
            const scope = mapDialogOptionToScope(value);
            const dueDate = event.dueDate;
            const result = handler.handleDeletion(
                original.rruleset,           // originalRuleStr
                new Date(dueDate ?? 0),     // deletedDate
                scope,                       // scope
                new Date(original.dueDate ?? 0)             // Add the originalDateTime parameter
            );

            if (result.originalAction === 'delete') {
                console.log('[DELETE_RECURRING] Deleting original message');
                if (original._id) dispatch(deleteUserMessage(original._id));
            } else if (result.originalAction === 'patch') {
                console.log('[DELETE_RECURRING] Patching original message with new rule:', result.originalNewRRule);
                // A survivor without a rule is a one-off and must say so on the wire —
                // see originalRecurrenceFields for why `undefined` kept the stale rule.
                const patchedMessage = { ...original, ...originalRecurrenceFields(result), dueDate: toUserMessageDate(result.originalDueDate), skipDayMappingFix: true };
                // BE#657 m2 — await+unwrap so scheduleMessage's missing-dueDate guard (it now
                // throws instead of coercing to "now") surfaces via the catch below rather than
                // becoming a swallowed rejected action.
                await dispatch(scheduleMessage(patchedMessage as ExtendedUserMessage)).unwrap();
            }
        } catch (error) {
            console.error('[DELETE_RECURRING] Error handling deletion:', error);
            throw error;
        }
    }
);

export interface RecurringDialogCloseThunkParams {
    value: confirmationDialogOption;
    event: EventPL;
}

export const onRecurringDialogCloseThunk = createAsyncThunk<any, RecurringDialogCloseThunkParams, { state: CommonRootState }>(
    "calendar/onRecurringDialogClose",
    async ({ value, event }, { getState, dispatch }) => {
        if (!value) return;

        const state = getState();
        const messages = userMessagesSelectByQuery(state.DBUserMessageReducer, {
            $or: [{ deleted: { $exists: false } }, { deleted: false }],
        });

        const original = messages.find((m) => m._id === event._id);
        if (!original?.rruleset) {
            console.log('[RECURRING_DIALOG] No original message or rruleset found');
            return;
        }

        try {
            const scope = mapDialogOptionToScope(value);
            const dueDate = event.dueDate;
            const originalDateTime = new Date(original.dueDate ?? 0);

            const result = handler.handleDragAndDrop(
                original.rruleset,                                    // originalRuleStr
                new Date(state.CalendarReducer.selectedEventDate ?? 0),   // draggedDate (original event date)
                new Date(dueDate ?? 0),                              // newDate (where event was dropped)
                scope,                                               // scope
                originalDateTime                                    // Add the originalDateTime parameter
            );

            // FE#151's ordering rule, which the DRAG path never got: create and confirm
            // the replacement BEFORE removing or rewriting the original. These used to be
            // two independent, unawaited dispatches, so on the scopes that delete the
            // original (a whole-series move, or moving from the first occurrence) the
            // create raced the delete. Whichever way that race fell, it fell badly: the
            // create landing first found the original still present and was deduped onto
            // it, and the delete then removed the only document either of them had — the
            // entire series gone, with no error anywhere.
            await applyRecurringEditPlan(result, {
                createReplacement: () => {
                    // Omit _id entirely so scheduleMessage takes the create path; an
                    // empty-string _id would survive serialization and fail ObjectId casting.
                    const { _id: _omittedId, ...eventWithoutId } = event;
                    const newMessage: ScheduleMessageThunkArg = {
                        ...eventWithoutId,
                        // The fields that decide whether the split-off occurrence survives
                        // are built in ONE place, which the recurrence harness calls too —
                        // see split-occurrence-document.ts for why that matters.
                        ...buildSplitOccurrenceFields(
                            result,
                            original.clientIdempotencyKey,
                            state.CalendarReducer.selectedEventDate,
                            toUserMessageDate,
                        ),
                    };
                    // #1133: drops the series' parentMessageId and resolves only for a STORED doc.
                    return storeRecurringReplacement(newMessage, doc => dispatch(scheduleMessage(doc)).unwrap());
                },
                deleteOriginal: () => dispatch(deleteUserMessage(original._id)).unwrap(),
                patchOriginal: () => {
                    // A head without a rule is a one-off and must say so on the wire —
                    // see originalRecurrenceFields for why `undefined` kept the stale rule.
                    const patchedMessage = { ...original, ...originalRecurrenceFields(result), dueDate: toUserMessageDate(result.originalDueDate), skipDayMappingFix: true };
                    return dispatch(scheduleMessage(patchedMessage as ExtendedUserMessage)).unwrap();
                },
            });
        } catch (error) {
            console.error('[RECURRING_DIALOG] Error handling drag and drop:', error);
            throw error;
        }
    }
);

// Type for lean event objects from the calendar
interface LeanCalendarEvent {
    _id: string;
    isCampaign: boolean;
    dueDate: string;
    isRecurring: boolean;
}

// Parameters for the flexible bridging thunk
interface FetchFullEventParams {
    leanEvent: LeanCalendarEvent;
    preserveInstanceDate?: boolean; // Use lean event's dueDate instead of original
    returnFullData?: boolean; // Return complete event data (default: true)
}

/**
 * Universal bridging thunk to fetch full event data from lean calendar events
 * Can be used by any operation that needs complete UserMessage or Campaign data
 */
export const fetchFullEventDataThunk = createAsyncThunk<any, FetchFullEventParams, { state: CommonRootState }>(
    "calendar/fetchFullEventData",
    async ({ leanEvent, preserveInstanceDate = true, returnFullData = true }, { getState, rejectWithValue }) => {
        const state = getState();

        if (leanEvent.isCampaign) {
            // For campaigns, we don't have campaign selectors yet, so return the lean event for now
            // TODO: Implement when campaign selectors are available
            return leanEvent;
        } else {
            // Fetch complete UserMessage data
            const fullUserMessage = userMessagesSelectOneObjectByQuery(state.DBUserMessageReducer, {
                _id: leanEvent._id
            });

            if (!fullUserMessage) {
                console.error(`UserMessage with ID ${leanEvent._id} not found in state`);
                return rejectWithValue('UserMessage not found');
            }

            if (!returnFullData) {
                // Return only minimal data if requested
                return {
                    _id: fullUserMessage._id,
                    dueDate: preserveInstanceDate ? leanEvent.dueDate : fullUserMessage.dueDate
                };
            }

            // Return complete data with optional date adjustment
            return {
                ...fullUserMessage,
                dueDate: preserveInstanceDate ? leanEvent.dueDate : fullUserMessage.dueDate
            };
        }
    }
);

/**
 * Specialized thunk for handling calendar event clicks to open the edit dialog
 * Uses the universal fetchFullEventDataThunk
 */
export const editCalendarEventThunk = createAsyncThunk<any, LeanCalendarEvent, { state: CommonRootState }>(
    "calendar/editCalendarEvent",
    async (leanEvent, { dispatch, rejectWithValue }) => {
        // Only handle UserMessages, campaigns are handled differently
        if (leanEvent.isCampaign) {
            return;
        }
        // A send that already went out is a record, not a plan. Opening it in the scheduling
        // editor offers "save", and saving a soft-deleted sent row would put it back in the
        // queue — a second send of something that already went out. It is shown, not edited.
        if ((leanEvent as { isSentRecord?: boolean }).isSentRecord === true) {
            return { refused: 'already-sent' };
        }

        try {
            await dispatch(setSelectedEventDate(leanEvent.dueDate));

            // Use the universal thunk to fetch full event data with instance date preserved
            const fullEventData = await dispatch(fetchFullEventDataThunk({
                leanEvent,
                preserveInstanceDate: true,
                returnFullData: true
            })).unwrap();

            // Preload the editor state immediately to avoid race conditions
            dispatch(scheduledMessageEditorActions.loadMessageData(fullEventData));
            // Dispatch the editCalendarMessage action with the complete UserMessage
            dispatch(addScheduledMessageButtonActions.editCalendarMessage(fullEventData));

            return { success: true };
        } catch (error) {
            console.error(`editCalendarEventThunk failed`, error);
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk to schedule a message from a lean calendar event
 * Handles data fetching internally to keep components simple
 */
export const scheduleMessageFromLeanEventThunk = createAsyncThunk<any, {
    leanEvent: LeanCalendarEvent | any; // Accept both lean and full events
    updates: Partial<UserMessage>
}, { state: CommonRootState }>(
    "calendar/scheduleMessageFromLeanEvent",
    async ({ leanEvent, updates }, { dispatch, rejectWithValue }) => {
        if (leanEvent.isCampaign) {
            console.warn('Cannot schedule campaign as UserMessage');
            return rejectWithValue('Cannot schedule campaign as UserMessage');
        }

        try {
            let fullEventData: UserMessage;

            // Check if we already have full data (has 'message' property)
            const hasFullData = 'message' in leanEvent;

            if (hasFullData) {
                // Already have full data, use it directly
                fullEventData = leanEvent as UserMessage;
            } else {
                // Need to fetch full event data
                fullEventData = await dispatch(fetchFullEventDataThunk({
                    leanEvent,
                    preserveInstanceDate: true,
                    returnFullData: true
                })).unwrap() as UserMessage;
            }

            // Apply updates and schedule
            const messageToSchedule: UserMessage = {
                ...fullEventData,
                ...updates
            };

            return dispatch(scheduleMessage(messageToSchedule as ExtendedUserMessage));
        } catch (error) {
            console.error('Error scheduling message from event:', error);
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk to delete a message from a lean calendar event
 * Handles both recurring and non-recurring messages appropriately
 */
export const deleteMessageFromLeanEventThunk = createAsyncThunk<any, {
    leanEvent: LeanCalendarEvent
}, { state: CommonRootState }>(
    "calendar/deleteMessageFromLeanEvent",
    async ({ leanEvent }, { dispatch, rejectWithValue }) => {
        if (leanEvent.isCampaign) {
            console.warn('Use campaign deletion thunk for campaigns');
            return rejectWithValue('Use campaign deletion thunk for campaigns');
        }

        try {
            if (leanEvent.isRecurring) {
                // For recurring messages, we need full data for the dialog
                // Return the full event data so the component can show the dialog
                const fullEventData = await dispatch(fetchFullEventDataThunk({
                    leanEvent,
                    preserveInstanceDate: true,
                    returnFullData: true
                })).unwrap();

                return { needsDialog: true, fullEventData };
            } else {
                // For non-recurring messages, delete directly
                return dispatch(deleteUserMessage(leanEvent._id));
            }
        } catch (error) {
            console.error('Error deleting message from lean event:', error);
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk to handle right-click operations on lean calendar events
 * Fetches full data and returns it for dialog operations
 */
export const handleRightClickFromLeanEventThunk = createAsyncThunk<any, {
    leanEvent: LeanCalendarEvent;
    domRect?: Partial<DOMRect>;
}, { state: CommonRootState }>(
    "calendar/handleRightClickFromLeanEvent",
    async ({ leanEvent, domRect }, { dispatch, rejectWithValue }) => {
        if (leanEvent.isCampaign) {
            // For campaigns, just return the lean event with positioning
            return { ...leanEvent, ...domRect };
        }

        try {
            // Fetch full event data for UserMessages
            const fullEventData = await dispatch(fetchFullEventDataThunk({
                leanEvent,
                preserveInstanceDate: true,
                returnFullData: true
            })).unwrap();

            // Combine full data with positioning info
            return { ...fullEventData, ...domRect };
        } catch (error) {
            console.error('Error handling right-click for lean event:', error);
            // Fallback to lean event
            return { ...leanEvent, ...domRect };
        }
    }
);

/**
 * Thunk to handle recurring event drag operations
 * Fetches full data and returns it ready for dialog, keeping components simple
 */
export const handleRecurringEventDragThunk = createAsyncThunk<any, {
    leanEvent: LeanCalendarEvent;
    newDueDate: string;
}, { state: CommonRootState }>(
    "calendar/handleRecurringEventDrag",
    async ({ leanEvent, newDueDate }, { dispatch, rejectWithValue }) => {
        if (leanEvent.isCampaign) {
            console.warn('Cannot handle campaign as recurring UserMessage');
            return rejectWithValue('Cannot handle campaign as recurring UserMessage');
        }

        try {
            // Store the original date for the recurring event modification
            await dispatch(setSelectedEventDate(leanEvent.dueDate));

            // Fetch full event data
            const fullEventData = await dispatch(fetchFullEventDataThunk({
                leanEvent,
                preserveInstanceDate: true,
                returnFullData: true
            })).unwrap() as UserMessage;

            // Return full event data with new dueDate for the dialog
            return {
                ...fullEventData,
                dueDate: newDueDate
            };
        } catch (error) {
            console.error('Error handling recurring event drag:', error);
            // Fallback to lean event with new date
            return {
                ...leanEvent,
                dueDate: newDueDate
            };
        }
    }
); 